import { InjectionToken } from '@angular/core';
import { HttpContextToken } from '@angular/common/http';

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
class SharedConstant {
    //Generic context to help with API URL path building
    static { this.JWT_TOKEN = "containerJwt"; }
    static { this.AMPXY_HOST = "ampxy_host"; }
    static { this.SUPPLYPOWER = "gmsupplypower.com"; }
    static { this.SPW002 = "SPW002"; }
    static { this.SECURITY_CONTEXT = "/api/security"; }
    // Common JSON Model Variables
    static { this.RESULT_LIST_PARAMETER = "resultList"; }
    static { this.DATA_LIST_PARAMETER = "data"; }
    static { this.PRIMARY_KEY_PARAMETER = "primaryKey"; }
    static { this.PAGING_PARAMETER = "paging"; }
    static { this.PRIMARY_KEY_PARAMETER_DEFAULT = "id"; }
    // User Variable Names
    static { this.MENU_LIST_PARAMETER = "menuList"; }
    static { this.USER_PARAMETER = "user"; }
    static { this.BUSINESS_USER_PARAMETER = "businessUser"; }
    static { this.APP_PARAMETER = "app"; }
    static { this.SITE_CODE_PARAMETER = "siteCodes"; }
    // Message Parameters
    static { this.SUCCESS_MESSAGE_PARAMETER = "message"; }
    static { this.INFORMATION_MESSAGE_PARAMETER = "informationMessages"; }
    static { this.ERROR_MESSAGE_PARAMETER = "errorList"; }
    static { this.SYSTEM_FAILURE_MESSAGE_PARAMETER = "systemFailureMessage"; }
    // Common APIs
    static { this.USER_SESSION_CONTROLLER_ENDPOINT = "/user/get/permissions"; }
    static { this.SITE_CODE_CONTROLLER_ENDPOINT = "/ldap/spld/user/get"; }
    // Time out Configurations
    static { this.MESSAGE_TIMEOUT_MILLISECONDS = 4000; } // 4 sec
    static { this.SESSION_TIMEOUT = 30; } // 30 min
    static { this.SESSION_TIMEOUT_WARNING = 25; } // 25 min
    static { this.SESSION_TIMER_INTERVAL = 1000; } // 1 sec
    static { this.SESSION_TIMER_DELAY = 5000; } // 5 sec
    // Time out Messages
    static { this.CONFIRM_SESSION_HEADER_DIALOG = "Warning"; }
    static { this.CONFIRM_SESSION_BODY_DIALOG_FIRST_LINE = "Your Session has timed out."; }
    static { this.CONFIRM_SESSION_BODY_DIALOG_SECOND_LINE = "Session expired, please refresh."; }
    static { this.CONFIRM_SESSION_REFRESH_DIALOG = "Refresh"; }
    // Breadcrumb Variable Name
    static { this.MAX_BREADCRUMB_LENGTH = 6; }
    static { this.ROUTE_DATA_FOR_BREADCRUMB = "breadcrumb"; }
    static { this.ROUTE_DATA_FOR_BREADCRUMB_ICON = "breadcrumbIcon"; }
    static { this.ROUTE_DATA_FOR_PAGE_TITLE = "title"; }
    static { this.ROUTE_DATA_FOR_BREADCRUMB_ROOT = "breadcrumbRoot"; }
    static { this.DEFAULT_GRID_FILTERING_DELAY = 500; }
    static { this.HTTP_GET_EXTENSION = "get"; }
    static { this.HTTP_LIST_EXTENSION = "list"; }
    static { this.HTTP_SEARCH_EXTENSION = "/search"; }
    static { this.HTTP_CREATE_EXTENSION = "create"; }
    static { this.HTTP_UPDATE_EXTENSION = "update"; }
    static { this.HTTP_DELETE_EXTENSION = "delete"; }
    static { this.SUPPLYPOWER_URL = "https://gmsupplypower.covisint.com/web/portal/home"; }
    static { this.LOGOUT_URL = "./assets/html/logout.html"; }
    static { this.NO_ACCESS_URL = "./assets/html/no-access.html"; }
    static { this.NO_ROLE_ASSIGN_ROUTE = "role-not-assign"; }
    static { this.MONTH_LIST = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]; }
    // Yes / No
    static { this.YES = 'Yes'; }
    static { this.NO = 'No'; }
    // Message constants - static messages for page navigation protection when the form data is not saved.
    static { this.UNSAVED_CHANGES_DIALOG_TITLE = 'Warning: Unsaved Changes'; }
    static { this.UNSAVED_CHANGES_MESSAGE = 'This page has unsaved data. Are you sure you want to leave the page?'; }
}

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
class GridConstant {
    static { this.FAB_STRING_FILTER_OPERATOR = [
        'contains',
        'doesnotcontain',
        'eq',
        'neq',
        'startswith',
        'endswith',
        'isempty',
        'isnotempty'
    ]; }
    static { this.FAB_NUMBER_FILTER_OPERATOR = ['eq', 'gte', 'gt', 'lte', 'lt', 'isempty', 'isnotempty']; }
    static { this.FAB_DATE_FILTER_OPERATOR = ['gte', 'gt', 'lte', 'lt', 'isempty', 'isnotempty']; }
}

class MessageConstant {
    //Static Messages for page navigation protection when the form data is not saved.
    static { this.ADD_PAGE_UNSAVED_MESSAGE = "Change has not been saved. Please click Add button to save change. Otherwise click Cancel button to close the page."; }
    static { this.EDIT_PAGE_UNSAVED_MESSAGE = "Change has not been saved. Please click Save button to save change. Otherwise click Cancel button to close the page."; }
    static { this.UPLOAD_PAGE_UNSAVED_MESSAGE = "Change has not been saved. Please click Upload button to save change. Otherwise click Cancel button to close the page."; }
    static { this.PROCESS_PAGE_UNSAVED_MESSAGE = "Saving has not been completed. Please wait for next few seconds to complete the process."; }
}

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
/**
 * @updated Marilyn Mao marilyn.mao@gm.com
 * @since 11/17/2022
 */
/**
 * Authentication Service Constants
 */
const USER_LOGIN_USER_KEY = '{userId}';
const PIN_LOGIN_PIN_KEY = '{pin}';
const OAUTH_TOKEN_PATH = 'oauth/token';
const OAUTH_GRANT_TYPE_PARAM = '?grant_type=';
const OAUTH_CLIENT_GRANT_TYPE = 'client_credentials';
const OATH_CLIENT_CREDENTIALS_PATH = OAUTH_TOKEN_PATH + OAUTH_GRANT_TYPE_PARAM + OAUTH_CLIENT_GRANT_TYPE;
const USER_LOGIN_PATH = OATH_CLIENT_CREDENTIALS_PATH + '&userId=' + USER_LOGIN_USER_KEY + '&pwd=';
const PIN_LOGIN_PATH = OATH_CLIENT_CREDENTIALS_PATH + '&pin=' + PIN_LOGIN_PIN_KEY;
const USER_AUTHENTICATION_TYPE = 'user';
const PIN_LOGIN_METHOD = 'pin';
const USER_CREDENTIALS_LOGIN_METHOD = 'userCredentials';
const APP_AUTHENTICATION_TYPE = 'app';
const DEFAULT_AUTHENTICATION_TYPE = new InjectionToken('default.auth');
const INTERCEPTOR_AUTH_TYPE_OVERRIDE = new HttpContextToken(() => undefined);

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
/**
 * Token Constants
 */
const REDIRECT_QUERY_PARAM = 'redirectPath';
const OATH_REFRESH_TOKEN_PATH = 'oauth/token?grant_type=refresh_token&refresh_token=';
const REFRESH_TOKEN_PATH = OATH_REFRESH_TOKEN_PATH;
const REFRESH_TOKEN_SECONDS_FROM_EXPIRY = 60;
const DEFAULT_COOKIE_DOMAIN = '.gm.com';
const PIN_ACCESS_KEY = 'pin';
const USER_ACCESS_KEY = 'user';
const APP_ACCESS_KEY = 'app';

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/**
 * Generated bundle index. Do not edit.
 */

export { APP_ACCESS_KEY, APP_AUTHENTICATION_TYPE, DEFAULT_AUTHENTICATION_TYPE, DEFAULT_COOKIE_DOMAIN, GridConstant, INTERCEPTOR_AUTH_TYPE_OVERRIDE, MessageConstant, OATH_CLIENT_CREDENTIALS_PATH, OATH_REFRESH_TOKEN_PATH, OAUTH_CLIENT_GRANT_TYPE, OAUTH_GRANT_TYPE_PARAM, OAUTH_TOKEN_PATH, PIN_ACCESS_KEY, PIN_LOGIN_METHOD, PIN_LOGIN_PATH, PIN_LOGIN_PIN_KEY, REDIRECT_QUERY_PARAM, REFRESH_TOKEN_PATH, REFRESH_TOKEN_SECONDS_FROM_EXPIRY, SharedConstant, USER_ACCESS_KEY, USER_AUTHENTICATION_TYPE, USER_CREDENTIALS_LOGIN_METHOD, USER_LOGIN_PATH, USER_LOGIN_USER_KEY };
